import React, {useEffect, useState, useCallback} from 'react';
import styled, {keyframes} from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {QRCodeSVG} from 'qrcode.react';
import {useLanguage} from '../contexts/LanguageContext';
import {useScrollZoom} from '../hooks/useScrollZoom';
import {firestoreService} from '../firebase/firestore';
import {TagDisplay} from './admin/forms/TagSelector';
import activity0 from '../assets/activity_0.jpg';
import activity1 from '../assets/activity_1.jpg';
import activity2 from '../assets/activity_2.jpg';
import activity3 from '../assets/activity_3.jpg';

const ActivitiesSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);

  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  color: var(--brand-primary);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
`;

const ActivitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;

  &.upcoming {
    grid-template-columns: 1fr;
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ActivityCard = styled.div`
  background: ${props => props.upcoming ? 'linear-gradient(135deg, var(--brand-light), var(--bg-primary))' : 'var(--bg-elevated)'};
  border: ${props => props.upcoming ? '2px solid var(--brand-primary)' : '1px solid var(--border-lighter)'};
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px var(--shadow);
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px var(--shadow-strong);
  }

  ${props => props.$clickable && `
    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 16px 36px var(--shadow-strong);
    }

    &:active {
      transform: translateY(-2px);
    }
  `}

  ${props => props.upcoming && `
    flex-direction: row;
    align-items: stretch;
    min-height: 200px;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 0;
      height: 0;
      border-left: 35px solid transparent;
      border-top: 35px solid var(--brand-primary);
      z-index: 1;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      min-height: auto;
    }
  `}
`;

const ActivityImageContainer = styled.div`
  height: 220px;
  overflow: hidden;

  @media (max-width: 480px) {
    height: 180px;
  }

  ${ActivityCard}.upcoming & {
    width: 300px;
    height: 100%;
    flex-shrink: 0;
    align-self: stretch;
    display: flex;

    @media (max-width: 768px) {
      width: 100%;
      height: 200px;
    }
  }
`;

const ActivityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ActivityCard}:hover & {
    transform: scale(1.1);
  }

  ${ActivityCard}.upcoming & {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 0;
  }
`;

const ActivityContent = styled.div`
  padding: 1.2rem 1.4rem;
  flex: 1;
  display: flex;
  flex-direction: column;

  ${ActivityCard}.upcoming & {
    justify-content: center;
  }
`;

const ActivityTitle = styled.h3`
  color: var(--brand-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
  line-height: 1.4;
`;

const ActivityDate = styled.div`
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  &.upcoming {
    color: var(--brand-primary);
    font-weight: 600;
  }
`;

const ActivityDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${ActivityCard}.upcoming & {
    -webkit-line-clamp: 5;
  }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, var(--bg-secondary) 25%, var(--bg-elevated) 50%, var(--bg-secondary) 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: ${props => props.$radius || '4px'};
  height: ${props => props.$height || '16px'};
  width: ${props => props.$width || '100%'};
`;

const SkeletonActivityCard = () => (
  <div style={{ background: 'var(--bg-elevated)', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.08)' }}>
    <SkeletonBlock $height="300px" $radius="0" />
    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <SkeletonBlock $height="20px" $width="60%" />
      <SkeletonBlock $height="14px" $width="100%" />
      <SkeletonBlock $height="14px" $width="80%" />
    </div>
  </div>
);

const RegisterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 20px;
  background: var(--brand-primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--brand-secondary);
    transform: translateY(-1px);
  }
`;

const CapacityInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-left: 12px;
`;

const ShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  color: var(--brand-primary);
  border: 1.5px solid var(--brand-primary);
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--brand-primary);
    color: white;
    transform: translateY(-1px);
  }
`;

const QROverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const QRModal = styled.div`
  background: var(--bg-elevated, #fff);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  max-width: 340px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const QRTitle = styled.h3`
  color: var(--brand-primary);
  font-size: 1.1rem;
  margin: 0 0 0.5rem;
`;

const QRSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin: 0 0 1.2rem;
`;

const QRCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.4rem;
  color: var(--text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const CopyLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 1rem;
  padding: 8px 20px;
  background: var(--brand-primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const Activities = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [upcomingActivities, setUpcomingActivities] = useState([]);
  const [pastActivities, setPastActivities] = useState([]);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollZoom = useScrollZoom({ disabled: reducedMotion });
  const [loading, setLoading] = useState(true);
  const [qrActivity, setQrActivity] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const defaultActivities = [
    {
      id: 1,
      image: activity0,
      name: { en: "Flower Gardens", vi: "Vườn hoa" },
      description: {
        en: "Visit Da Lat's famous flower gardens and experience the beauty of the highland blooms",
        vi: "Thăm những vườn hoa nổi tiếng của Đà Lạt và trải nghiệm vẻ đẹp của những bông hoa cao nguyên"
      },
      tags: ["popular", "relaxing"]
    },
    {
      id: 2,
      image: activity1,
      name: { en: "Mountain Adventures", vi: "Phiêu lưu núi rừng" },
      description: {
        en: "Explore the stunning landscapes and breathe in the fresh mountain air",
        vi: "Khám phá những cảnh quan tuyệt đẹp và hít thở không khí trong lành của núi rừng"
      },
      tags: ["adventure", "featured"]
    },
    {
      id: 3,
      image: activity2,
      name: { en: "Local Markets", vi: "Chợ địa phương" },
      description: {
        en: "Discover local culture and taste authentic Vietnamese highland cuisine",
        vi: "Khám phá văn hóa địa phương và thưởng thức ẩm thực cao nguyên Việt Nam đích thực"
      },
      tags: ["cultural", "family"]
    },
    {
      id: 4,
      image: activity3,
      name: { en: "Coffee Plantations", vi: "Vườn cà phê" },
      description: {
        en: "Experience the world-famous Vietnamese coffee culture in the highlands",
        vi: "Trải nghiệm văn hóa cà phê Việt Nam nổi tiếng thế giới trên cao nguyên"
      },
      tags: ["bestseller", "cultural"]
    }
  ];

  const getBilingualText = (textObj, fallback = '') => {
    if (typeof textObj === 'string') return textObj;
    if (typeof textObj === 'object' && textObj !== null) {
      return textObj[currentLanguage] || textObj.en || textObj.vi || fallback;
    }
    return fallback;
  };

  const handleActivityClick = (activity) => {
    if (activity.detailUrl) {
      window.open(activity.detailUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getShareUrl = useCallback((activity) => {
    if (activity.registrationEnabled) {
      return `${window.location.origin}/event/${activity.id}`;
    }
    if (activity.detailUrl) {
      return activity.detailUrl;
    }
    return `${window.location.origin}/#activities`;
  }, []);

  const handleShare = useCallback(async (e, activity) => {
    e.stopPropagation();
    const url = getShareUrl(activity);
    const title = typeof activity.name === 'object'
      ? (activity.name[currentLanguage] || activity.name.en || activity.name.vi)
      : (activity.name || 'Activity');

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    setQrActivity(activity);
    setCopySuccess(false);
  }, [getShareUrl, currentLanguage]);

  const handleCopyLink = useCallback(async () => {
    if (!qrActivity) return;
    const url = getShareUrl(qrActivity);
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [qrActivity, getShareUrl]);

  useEffect(() => {
    const loadActivities = async () => {
      try {
        const firestoreActivities = await firestoreService.getAll('activities');
        let allActivities = firestoreActivities && firestoreActivities.length > 0 ? firestoreActivities : defaultActivities;

        // Filter out hidden activities
        allActivities = allActivities.filter(a => !a.hidden);

        // Split activities into upcoming and past
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day for comparison
        
        const upcoming = [];
        const past = [];
        
        allActivities.forEach(activity => {
          const activityDate = new Date(activity.date || activity.createdAt || 0);
          activityDate.setHours(0, 0, 0, 0);
          
          if (activityDate >= today) {
            upcoming.push(activity);
          } else {
            past.push(activity);
          }
        });
        
        // Sort upcoming activities by date (earliest first)
        upcoming.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateA - dateB;
        });
        
        // Sort past activities by date (newest first)
        past.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateB - dateA;
        });
        
        setUpcomingActivities(upcoming);
        setPastActivities(past);
        setActivities(allActivities); // Keep for backward compatibility
      } catch (error) {
        console.error('Error loading activities:', error);
        setActivities(defaultActivities);
      } finally {
        setLoading(false);
      }
    };

    loadActivities();
  }, []);

  if (loading) {
    return (
      <ActivitiesSection id="activities">
        <Container>
          <SectionHeader>
            <SectionTitle>{t("Things to Do in Da Lat", "Hoạt động tại Đà Lạt")}</SectionTitle>
            <SectionSubtitle>{t("Explore the wonders of the City of Eternal Spring", "Khám phá những kỳ quan của Thành phố Ngàn hoa")}</SectionSubtitle>
          </SectionHeader>
          <ActivitiesGrid>
            {[1, 2, 3, 4].map(i => <SkeletonActivityCard key={i} />)}
          </ActivitiesGrid>
        </Container>
      </ActivitiesSection>
    );
  }

  return (
    <ActivitiesSection id="activities">
      <Container>
        <SectionHeader data-aos="fade-up" ref={scrollZoom.ref} style={scrollZoom.style}>
          <SectionTitle>
            {t("Things to Do in Da Lat", "Hoạt động tại Đà Lạt")}
          </SectionTitle>
          <SectionSubtitle>
            {t("Explore the wonders of the City of Eternal Spring", "Khám phá những kỳ quan của Thành phố Ngàn hoa")}
          </SectionSubtitle>
        </SectionHeader>

        {/* Upcoming Activities Section */}
        {upcomingActivities.length > 0 && (
          <>
            <SectionHeader style={{ marginBottom: '2rem', marginTop: '2rem' }}>
              <SectionTitle style={{ fontSize: '2rem' }}>
                {t("🌟 Upcoming Activities", "🌟 Hoạt động sắp tới")}
              </SectionTitle>
              <SectionSubtitle>
                {t("Don't miss these exciting upcoming events!", "Đừng bỏ lỡ những sự kiện thú vị sắp tới!")}
              </SectionSubtitle>
            </SectionHeader>
            
            <ActivitiesGrid className="upcoming">
              {upcomingActivities.map((activity, index) => {
                const activityTitle = getBilingualText(activity.name || activity.title, 'Activity');
                const activityDescription = getBilingualText(activity.description, '');
                const activityImage = activity.imageUrl || activity.image || activity0;
                
                return (
                  <ActivityCard
                    key={`upcoming-${activity.id || index}`}
                    upcoming={true}
                    data-aos="fade-up"
                    $clickable={!!activity.detailUrl}
                    onClick={() => handleActivityClick(activity)}
                  >
                    <ActivityImageContainer>
                      <ActivityImage src={activityImage} alt={activityTitle} loading="lazy" />
                    </ActivityImageContainer>
                    <ActivityContent>
                      <ActivityTitle>{activityTitle}</ActivityTitle>
                      {activity.date && (
                        <ActivityDate className="upcoming">
                          <i className="fas fa-calendar-alt"></i>
                          {new Date(activity.date).toLocaleDateString()}
                        </ActivityDate>
                      )}
                      {activity.tags && activity.tags.length > 0 && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <TagDisplay tags={activity.tags} type="activity" />
                        </div>
                      )}
                      <ActivityDescription>{activityDescription}</ActivityDescription>
                      {activity.detailUrl && (
                        <div style={{
                          marginTop: '0.8rem',
                          fontSize: '0.85rem',
                          color: 'var(--brand-primary)',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}>
                          <i className="fas fa-external-link-alt" style={{ fontSize: '0.75rem' }}></i>
                          {t("View details", "Xem chi tiết")}
                        </div>
                      )}
                      {activity.registrationEnabled && activity.registrationStatus === 'open' && (
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                          <RegisterButton onClick={(e) => { e.stopPropagation(); navigate(`/event/${activity.id}`); }}>
                            <i className="fas fa-pen-to-square"></i>
                            {t("Register Now", "Đăng ký ngay")}
                          </RegisterButton>
                          {activity.capacity && (
                            <CapacityInfo>
                              <i className="fas fa-users"></i>
                              {activity.registrationCount || 0}/{activity.capacity}
                            </CapacityInfo>
                          )}
                        </div>
                      )}
                      <div style={{ marginTop: '12px' }}>
                        <ShareButton onClick={(e) => handleShare(e, activity)}>
                          <i className="fas fa-share-nodes"></i>
                          {t("Share", "Chia se")}
                        </ShareButton>
                      </div>
                    </ActivityContent>
                  </ActivityCard>
                );
              })}
            </ActivitiesGrid>
          </>
        )}

        {/* Past Activities Section */}
        {pastActivities.length > 0 && (
          <>
            <SectionHeader style={{ marginBottom: '2rem', marginTop: '3rem' }}>
              <SectionTitle style={{ fontSize: '2rem', color: 'var(--text-secondary)' }}>
                {t("📚 Past Activities", "📚 Hoạt động đã qua")}
              </SectionTitle>
              <SectionSubtitle>
                {t("Take a look at our previous events and experiences", "Xem lại những sự kiện và trải nghiệm trước đây")}
              </SectionSubtitle>
            </SectionHeader>
            
            <ActivitiesGrid>
              {pastActivities.map((activity, index) => {
                const activityTitle = getBilingualText(activity.name || activity.title, 'Activity');
                const activityDescription = getBilingualText(activity.description, '');
                const activityImage = activity.imageUrl || activity.image || activity0;
                
                return (
                  <ActivityCard
                    key={`past-${activity.id || index}`}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    $clickable={!!activity.detailUrl}
                    onClick={() => handleActivityClick(activity)}
                  >
                    <ActivityImageContainer>
                      <ActivityImage src={activityImage} alt={activityTitle} loading="lazy" />
                    </ActivityImageContainer>
                    <ActivityContent>
                      <ActivityTitle>{activityTitle}</ActivityTitle>
                      {activity.date && (
                        <ActivityDate>
                          <i className="fas fa-calendar-alt"></i>
                          {new Date(activity.date).toLocaleDateString()}
                        </ActivityDate>
                      )}
                      <ActivityDescription>{activityDescription}</ActivityDescription>
                      {activity.tags && activity.tags.length > 0 && (
                        <div style={{ marginTop: 'auto', paddingTop: '0.6rem' }}>
                          <TagDisplay tags={activity.tags} type="activity" />
                        </div>
                      )}
                      {activity.detailUrl && (
                        <div style={{
                          marginTop: '0.6rem',
                          fontSize: '0.85rem',
                          color: 'var(--brand-primary)',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}>
                          <i className="fas fa-external-link-alt" style={{ fontSize: '0.75rem' }}></i>
                          {t("View details", "Xem chi tiết")}
                        </div>
                      )}
                    </ActivityContent>
                  </ActivityCard>
                );
              })}
            </ActivitiesGrid>
          </>
        )}
      </Container>

      {qrActivity && (
        <QROverlay onClick={() => setQrActivity(null)}>
          <QRModal onClick={(e) => e.stopPropagation()}>
            <QRCloseButton onClick={() => setQrActivity(null)} aria-label="Close">&times;</QRCloseButton>
            <QRTitle>
              {typeof qrActivity.name === 'object'
                ? (qrActivity.name[currentLanguage] || qrActivity.name.en || qrActivity.name.vi)
                : qrActivity.name}
            </QRTitle>
            <QRSubtitle>{t("Scan to view or share", "Quet de xem hoac chia se")}</QRSubtitle>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', display: 'inline-block' }}>
              <QRCodeSVG
                value={getShareUrl(qrActivity)}
                size={200}
                level="M"
                marginSize={0}
              />
            </div>
            <div>
              <CopyLinkButton onClick={handleCopyLink}>
                <i className={copySuccess ? "fas fa-check" : "fas fa-link"}></i>
                {copySuccess ? t("Copied!", "Da sao chep!") : t("Copy Link", "Sao chep lien ket")}
              </CopyLinkButton>
            </div>
          </QRModal>
        </QROverlay>
      )}
    </ActivitiesSection>
  );
};

export default Activities;